import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { usePendingChanges, useDecidePendingChange } from '../../hooks';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { Check, X } from 'lucide-react';

interface PendingChange {
  change_id: string;
  target_table: string;
  operation: string;
  payload: Record<string, unknown>;
  status: string;
  submitted_by?: string;
  created_at: string;
}

/** Content-only version used by the People hub. */
export function OwnerPendingChangesContent() {
  const { data, isLoading } = usePendingChanges() as { data: PendingChange[] | undefined; isLoading: boolean };
  const decide = useDecidePendingChange();
  const toast = useToast();
  const [rejectFor, setRejectFor] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const list = (data ?? []).filter((c) => c.status === 'pending');

  async function approve(change_id: string) {
    try {
      await decide.mutateAsync({ change_id, decision: 'approved' });
      toast.success('อนุมัติแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function submitReject() {
    if (!rejectFor || !rejectNote.trim()) { toast.error('ระบุเหตุผลที่ปฏิเสธ'); return; }
    try {
      await decide.mutateAsync({ change_id: rejectFor, decision: 'rejected', decision_note: rejectNote.trim() });
      setRejectFor(null);
      setRejectNote('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  if (isLoading) return <p className="py-8 text-center text-gray-500">กำลังโหลด...</p>;

  return (
    <>
      <p className="text-sm text-gray-600 mb-3">การแก้ไขที่ HR ส่งมาขอ Owner อนุมัติ</p>
      <div className="space-y-2">
        {list.map((c) => (
          <div key={c.change_id} className="card p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-medium">{c.target_table} · {c.operation}</p>
                {c.submitted_by && (
                  <p className="text-xs text-gray-500">โดย: {c.submitted_by}</p>
                )}
                <p className="text-xs text-gray-500">{new Date(c.created_at).toLocaleString('th-TH')}</p>
              </div>
              <span className="badge-warn">{c.status}</span>
            </div>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto mb-3">
              {JSON.stringify(c.payload, null, 2)}
            </pre>
            <div className="flex gap-2">
              <button
                onClick={() => approve(c.change_id)}
                disabled={decide.isPending}
                className="btn-cta flex-1"
              >
                <Check className="w-4 h-4" /> อนุมัติ
              </button>
              <button
                onClick={() => { setRejectFor(c.change_id); setRejectNote(''); }}
                disabled={decide.isPending}
                className="btn-danger"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <p className="text-center text-gray-500 py-8">ไม่มีรายการรออนุมัติ</p>
        )}
      </div>

      <Modal open={rejectFor !== null} onClose={() => setRejectFor(null)} title="ปฏิเสธการเปลี่ยนแปลง">
        <div className="space-y-3">
          <div>
            <label className="label">เหตุผลที่ปฏิเสธ</label>
            <textarea className="input" rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="ระบุเหตุผล…" />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setRejectFor(null)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitReject} disabled={decide.isPending}>ปฏิเสธ</button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Standalone page kept for legacy /owner/pending-changes (now redirects via People hub). */
export function OwnerPendingChanges() {
  return (
    <AdminLayout title="อนุมัติการเปลี่ยนแปลง">
      <OwnerPendingChangesContent />
    </AdminLayout>
  );
}
